import type { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import {
  paletteHelper,
  spacingHelper,
  typographyHelper,
  Text,
  Inline,
  Badge,
  mediaHelper,
  boxShadow,
  radiusHelper,
} from '@property.com.au/ui-kit';
import { mapBadgeVariant } from '../../../../../app-kit/helpers/mapBadgeVariant';
import { LineClamp } from '../../../../../app-kit/helpers/LineClamp.styled';
import { clickThroughSourceTracking } from '../../../../../analytics/click-through-tracking/clickThroughSourceTracking';
import type { ClickThroughSourceLocalStorageValue } from '../../../../../analytics/click-through-tracking/TrackingSource';
import type { BadgeVariant } from '../../../../../app-kit/helpers/mapBadgeVariant';
import { PcaLink } from '../../../../../app-kit/components/PcaLink/PcaLink';

export interface Tile extends CardContentsProps {
  clickThroughTracking?: ClickThroughSourceLocalStorageValue | null;
  propertyPath?: string | null;
}
interface BadgeProps {
  text: string;
  variant: BadgeVariant;
}
interface CardContentsProps {
  badge: BadgeProps;
  description: string;
  subtitle: string;
  title: string;
}
interface NearbyPlanningPermitTilesProps {
  tiles: Tile[];
  isCollapsed: boolean;
  mobileVisibleTilesLimit: number;
  desktopVisibleTilesLimit: number;
}

const tilesGap = spacingHelper('extraSmall');

const TilesWrapper = styled.div<{
  $isCollapsed: boolean;
  $mobileVisibleTilesLimit: number;
  $desktopVisibleTilesLimit: number;
}>`
  display: flex;
  padding-bottom: ${spacingHelper('extraSmall')};
  gap: ${tilesGap};
  flex-wrap: wrap;
  ${({ $isCollapsed, $mobileVisibleTilesLimit, $desktopVisibleTilesLimit }) =>
    $isCollapsed &&
    css`
      ${mediaHelper('tabletMax')} {
        > article:nth-child(n + ${$mobileVisibleTilesLimit + 1}) {
          display: none;
        }
      }

      ${mediaHelper('desktopMin')} {
        > article:nth-child(n + ${$desktopVisibleTilesLimit + 1}) {
          display: none;
        }
      }
    `}
`;

const StyledCard = styled.article`
  ${boxShadow.defaultBoxShadow};
  &:hover {
    ${boxShadow.boxShadowHover}
  }
  transition: box-shadow 0.15s;
  background-color: ${paletteHelper('backgroundPrimary')};
  padding: ${spacingHelper('medium')};
  flex: 0 0 100%;
  max-width: 100%;

  ${mediaHelper('desktopMin')} {
    flex: 0 0 calc(50% - (${tilesGap} / 2));
    max-width: calc(50% - (${tilesGap} / 2));
  }

  border-radius: ${radiusHelper('medium')};
`;

const Title = styled(Text)`
  ${typographyHelper('body01Bold')};
  color: ${paletteHelper('linkPrimary')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subtitle = styled(LineClamp)`
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textSecondary')};
  margin-bottom: ${spacingHelper('medium')};
`;

const Description = styled(LineClamp)`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};
`;

const CardContents: FunctionComponent<CardContentsProps> = ({
  title,
  subtitle,
  description,
  badge,
}) => {
  return (
    <>
      <Inline gap="extraSmall" grow={false} justifyContent="space-between">
        <Title title={title}>{title}</Title>
        <Badge variant={mapBadgeVariant(badge.variant)}>{badge.text}</Badge>
      </Inline>
      <Subtitle title={subtitle} $numLinesToClamp={1}>
        {subtitle}
      </Subtitle>
      <Description $numLinesToClamp={2} title={description}>
        {description}
      </Description>
    </>
  );
};

const NearbyPlanningPermitTile: FunctionComponent<Tile> = ({
  propertyPath,
  clickThroughTracking,
  ...rest
}) => {
  if (propertyPath) {
    return (
      <StyledCard>
        <PcaLink
          anchorVariant="plain"
          data-test-id={`nearby-planning-permit-link-${propertyPath}`}
          onClick={() => clickThroughSourceTracking(clickThroughTracking)}
          to={propertyPath}
        >
          <CardContents {...rest} />
        </PcaLink>
      </StyledCard>
    );
  }
  return (
    <StyledCard>
      <CardContents {...rest} />
    </StyledCard>
  );
};

export const NearbyPlanningPermitTiles: FunctionComponent<NearbyPlanningPermitTilesProps> = ({
  tiles,
  isCollapsed,
  mobileVisibleTilesLimit,
  desktopVisibleTilesLimit,
}) => {
  return (
    <TilesWrapper
      $isCollapsed={isCollapsed}
      $mobileVisibleTilesLimit={mobileVisibleTilesLimit}
      $desktopVisibleTilesLimit={desktopVisibleTilesLimit}
    >
      {tiles.map((tile) => (
        <NearbyPlanningPermitTile key={tile.title} {...tile} />
      ))}
    </TilesWrapper>
  );
};
